import {isTextBlock} from '@portabletext/schema'
import type {EditorSelector} from '../editor/editor-selector'
import {isSelectionExpanded} from '../selectors'
import {getFocusTextBlock} from '../selectors/selector.get-focus-text-block'
import {getLastBlock} from '../selectors/selector.get-last-block'
import {getBlockEndPoint} from '../utils/util.get-block-end-point'
import {getBlockStartPoint} from '../utils/util.get-block-start-point'
import {isEmptyTextBlock} from '../utils/util.is-empty-text-block'
import {isEqualSelectionPoints} from '../utils/util.is-equal-selection-points'
import {sliceTextBlock} from '../utils/util.slice-text-block'
import {raise} from './behavior.types.action'
import {defineBehavior} from './behavior.types.behavior'

function getUniqueBlockKey(
  blockKey: string | undefined,
): EditorSelector<string> {
  return (snapshot) => {
    if (!blockKey) {
      return snapshot.context.keyGenerator()
    }

    if (snapshot.blockIndexMap.has(blockKey)) {
      return snapshot.context.keyGenerator()
    }

    return blockKey
  }
}

export const abstractInsertBehaviors = [
  defineBehavior({
    on: 'insert.blocks',
    guard: ({event}) => {
      const onlyBlock =
        event.blocks.length === 1 ? event.blocks.at(0) : undefined

      if (!onlyBlock) {
        return false
      }

      return {onlyBlock}
    },
    actions: [
      ({event}, {onlyBlock}) => [
        raise({
          type: 'insert.block',
          block: onlyBlock,
          placement: event.placement,
          select: event.select ?? 'end',
        }),
      ],
    ],
  }),
  defineBehavior({
    on: 'insert.blocks',
    guard: ({snapshot, event}) => {
      if (event.placement !== 'before') {
        return false
      }

      const firstBlockKey = getUniqueBlockKey(event.blocks.at(0)?._key)(
        snapshot,
      )
      const lastBlockKey = getUniqueBlockKey(event.blocks.at(-1)?._key)(
        snapshot,
      )

      return {firstBlockKey, lastBlockKey}
    },
    actions: [
      ({snapshot, event}, {firstBlockKey, lastBlockKey}) => [
        ...event.blocks.map((block, index) =>
          raise({
            type: 'insert.block',
            block:
              index === 0
                ? {
                    ...block,
                    _key: firstBlockKey,
                  }
                : index === event.blocks.length - 1
                  ? {
                      ...block,
                      _key: lastBlockKey,
                    }
                  : block,
            placement: index === 0 ? 'before' : 'after',
            select: index !== event.blocks.length - 1 ? 'end' : 'none',
          }),
        ),
        ...(event.select === 'none'
          ? [
              raise({
                type: 'select',
                at: snapshot.context.selection,
              }),
            ]
          : event.select === 'start'
            ? [
                raise({
                  type: 'select.block',
                  at: [{_key: firstBlockKey}],
                  select: 'start',
                }),
              ]
            : [
                raise({
                  type: 'select.block',
                  at: [{_key: lastBlockKey}],
                  select: 'end',
                }),
              ]),
      ],
    ],
  }),
  defineBehavior({
    on: 'insert.blocks',
    guard: ({snapshot, event}) => {
      if (event.placement !== 'after') {
        return false
      }

      const firstBlockKey = getUniqueBlockKey(event.blocks.at(0)?._key)(
        snapshot,
      )
      const lastBlockKey = getUniqueBlockKey(event.blocks.at(-1)?._key)(
        snapshot,
      )

      return {firstBlockKey, lastBlockKey}
    },
    actions: [
      ({snapshot, event}, {firstBlockKey, lastBlockKey}) => [
        ...event.blocks.map((block, index) =>
          raise({
            type: 'insert.block',
            block:
              index === 0
                ? {
                    ...block,
                    _key: firstBlockKey,
                  }
                : index === event.blocks.length - 1
                  ? {
                      ...block,
                      _key: lastBlockKey,
                    }
                  : block,
            placement: 'after',
            select: index !== event.blocks.length - 1 ? 'end' : 'none',
          }),
        ),
        ...(event.select === 'none'
          ? [
              raise({
                type: 'select',
                at: snapshot.context.selection,
              }),
            ]
          : event.select === 'start'
            ? [
                raise({
                  type: 'select.block',
                  at: [{_key: firstBlockKey}],
                  select: 'start',
                }),
              ]
            : [
                raise({
                  type: 'select.block',
                  at: [{_key: lastBlockKey}],
                  select: 'end',
                }),
              ]),
      ],
    ],
  }),
  defineBehavior({
    on: 'insert.blocks',
    guard: ({snapshot, event}) => {
      if (event.placement !== 'auto') {
        return false
      }

      const focusTextBlock = getFocusTextBlock(snapshot)

      if (!focusTextBlock) {
        return false
      }

      if (!isEmptyTextBlock(snapshot.context, focusTextBlock.node)) {
        return false
      }

      const firstBlockKey = getUniqueBlockKey(event.blocks.at(0)?._key)(
        snapshot,
      )
      const lastBlockKey = getUniqueBlockKey(event.blocks.at(-1)?._key)(
        snapshot,
      )

      return {focusTextBlock, firstBlockKey, lastBlockKey}
    },
    actions: [
      ({event}, {firstBlockKey, lastBlockKey}) => [
        ...event.blocks.map((block, index) =>
          raise({
            type: 'insert.block',
            block:
              index === 0
                ? {
                    ...block,
                    _key: firstBlockKey,
                  }
                : index === event.blocks.length - 1
                  ? {
                      ...block,
                      _key: lastBlockKey,
                    }
                  : block,
            placement: index === 0 ? 'auto' : 'after',
            select: index !== event.blocks.length - 1 ? 'end' : 'none',
          }),
        ),
        ...(event.select === 'none'
          ? [
              raise({
                type: 'select.block',
                at: [{_key: firstBlockKey}],
                select: 'start',
              }),
            ]
          : event.select === 'start'
            ? [
                raise({
                  type: 'select.block',
                  at: [{_key: firstBlockKey}],
                  select: 'start',
                }),
              ]
            : [
                raise({
                  type: 'select.block',
                  at: [{_key: lastBlockKey}],
                  select: 'end',
                }),
              ]),
      ],
    ],
  }),
  defineBehavior({
    on: 'insert.blocks',
    guard: ({snapshot, event}) => {
      if (event.placement !== 'auto') {
        return false
      }

      const focusTextBlock = getFocusTextBlock(snapshot)

      if (!focusTextBlock || !snapshot.context.selection) {
        return false
      }

      const focusBlockStartPoint = getBlockStartPoint({
        context: snapshot.context,
        block: focusTextBlock,
      })
      const focusBlockEndPoint = getBlockEndPoint({
        context: snapshot.context,
        block: focusTextBlock,
      })
      const focusTextBlockAfter = sliceTextBlock({
        context: {
          schema: snapshot.context.schema,
          selection: {
            anchor: snapshot.context.selection.focus,
            focus: focusBlockEndPoint,
          },
        },
        block: focusTextBlock.node,
      })
      const firstBlockKey = getUniqueBlockKey(event.blocks.at(0)?._key)(
        snapshot,
      )
      const lastBlockKey = getUniqueBlockKey(event.blocks.at(-1)?._key)(
        snapshot,
      )

      const isFirstBlockTextBlock = isTextBlock(
        snapshot.context,
        event.blocks.at(0),
      )

      return {
        firstBlockKey,
        lastBlockKey,
        focusBlockStartPoint,
        focusBlockEndPoint,
        focusTextBlockAfter,
        selection: snapshot.context.selection,
        isFirstBlockTextBlock,
      }
    },
    actions: [
      (
        {snapshot, event},
        {
          focusBlockEndPoint,
          focusTextBlockAfter,
          selection,
          firstBlockKey,
          lastBlockKey,
          focusBlockStartPoint,
          isFirstBlockTextBlock,
        },
      ) => [
        ...event.blocks.flatMap((block, index) =>
          index === 0
            ? [
                ...(isEqualSelectionPoints(selection.focus, focusBlockEndPoint)
                  ? []
                  : [
                      raise({
                        type: 'delete',
                        at: {
                          anchor: selection.focus,
                          focus: focusBlockEndPoint,
                        },
                      }),
                    ]),
                raise({
                  type: 'insert.block',
                  block: {
                    ...block,
                    _key: firstBlockKey,
                  },
                  placement: 'auto',
                  select: 'end',
                }),
              ]
            : index === event.blocks.length - 1
              ? [
                  raise({
                    type: 'insert.block',
                    block: {
                      ...block,
                      _key: lastBlockKey,
                    },
                    placement: 'after',
                    select: 'end',
                  }),
                  ...(!isEmptyTextBlock(snapshot.context, focusTextBlockAfter)
                    ? [
                        raise({
                          type: 'insert.block',
                          block: focusTextBlockAfter,
                          placement: 'auto',
                          select: event.select === 'end' ? 'none' : 'end',
                        }),
                      ]
                    : []),
                ]
              : [
                  raise({
                    type: 'insert.block',
                    block,
                    placement: 'after',
                    select: 'end',
                  }),
                ],
        ),
        ...(event.select === 'none'
          ? [
              raise({
                type: 'select',
                at: selection,
              }),
            ]
          : event.select === 'start'
            ? [
                isEqualSelectionPoints(selection.focus, focusBlockStartPoint) ||
                !isFirstBlockTextBlock
                  ? raise({
                      type: 'select.block',
                      at: [{_key: firstBlockKey}],
                      select: 'start',
                    })
                  : raise({
                      type: 'select',
                      at: {
                        anchor: selection.focus,
                        focus: selection.focus,
                      },
                    }),
              ]
            : []),
      ],
    ],
  }),
  defineBehavior({
    on: 'insert.blocks',
    guard: ({snapshot, event}) => {
      if (event.placement !== 'auto') {
        return false
      }

      const selection = snapshot.context.selection
      const firstBlockKey = getUniqueBlockKey(event.blocks.at(0)?._key)(
        snapshot,
      )
      const lastBlockKey = getUniqueBlockKey(event.blocks.at(-1)?._key)(
        snapshot,
      )

      return {firstBlockKey, lastBlockKey, selection}
    },
    actions: [
      ({event}, {firstBlockKey, lastBlockKey, selection}) => [
        ...event.blocks.map((block, index) =>
          raise({
            type: 'insert.block',
            block:
              index === 0
                ? {
                    ...block,
                    _key: firstBlockKey,
                  }
                : index === event.blocks.length - 1
                  ? {
                      ...block,
                      _key: lastBlockKey,
                    }
                  : block,
            placement: index === 0 ? 'auto' : 'after',
            select:
              index !== event.blocks.length - 1
                ? 'end'
                : (event.select ?? 'end'),
          }),
        ),
        ...(event.select === 'none'
          ? [
              raise({
                type: 'select',
                at: selection,
              }),
            ]
          : event.select === 'start'
            ? [
                raise({
                  type: 'select.block',
                  at: [{_key: firstBlockKey}],
                  select: 'start',
                }),
              ]
            : [
                raise({
                  type: 'select.block',
                  at: [{_key: lastBlockKey}],
                  select: 'end',
                }),
              ]),
      ],
    ],
  }),
  defineBehavior({
    on: 'insert.break',
    actions: [() => [raise({type: 'split'})]],
  }),
  defineBehavior({
    on: 'insert.child',
    guard: ({snapshot}) => {
      const lastBlock = getLastBlock(snapshot)

      if (!lastBlock) {
        return false
      }

      if (snapshot.context.selection) {
        return false
      }

      const lastBlockEndPoint = getBlockEndPoint({
        context: snapshot.context,
        block: lastBlock,
      })

      return {lastBlockEndPoint}
    },
    actions: [
      ({event}, {lastBlockEndPoint}) => [
        raise({
          type: 'select',
          at: {
            anchor: lastBlockEndPoint,
            focus: lastBlockEndPoint,
          },
        }),
        raise(event),
      ],
    ],
  }),
  defineBehavior({
    on: 'insert.child',
    guard: ({snapshot}) => {
      const focusTextBlock = getFocusTextBlock(snapshot)

      return snapshot.context.selection && !focusTextBlock
    },
    actions: [
      ({snapshot, event}) => [
        raise({
          type: 'insert.block',
          block: {
            _type: snapshot.context.schema.block.name,
            children: [
              {
                _type: snapshot.context.schema.span.name,
                text: '',
                marks: [],
              },
            ],
          },
          placement: 'auto',
          select: 'end',
        }),
        raise(event),
      ],
    ],
  }),
  defineBehavior({
    on: 'insert.inline object',
    actions: [
      ({event}) => [
        raise({
          type: 'insert.child',
          child: {
            _type: event.inlineObject.name,
            ...event.inlineObject.value,
          },
        }),
      ],
    ],
  }),
  defineBehavior({
    on: 'insert.soft break',
    actions: [() => [raise({type: 'insert.text', text: '\n'})]],
  }),
  defineBehavior({
    on: 'insert.span',
    guard: ({snapshot}) => !getFocusTextBlock(snapshot),
    actions: [
      ({snapshot, event}) => [
        raise({
          type: 'insert.block',
          block: {
            _type: snapshot.context.schema.block.name,
            children: [
              {
                _type: snapshot.context.schema.span.name,
                text: '',
                marks: [],
              },
            ],
          },
          placement: 'auto',
          select: 'end',
        }),
        raise(event),
      ],
    ],
  }),
  defineBehavior({
    on: 'insert.span',
    guard: ({snapshot, event}) => {
      const focusTextBlock = getFocusTextBlock(snapshot)
      const markDefs =
        event.annotations?.map((annotation) => ({
          _type: annotation.name,
          _key: snapshot.context.keyGenerator(),
          ...annotation.value,
        })) ?? []

      return {markDefs, focusTextBlock}
    },
    actions: [
      ({snapshot, event}, {markDefs, focusTextBlock}) => [
        ...(focusTextBlock
          ? [
              raise({
                type: 'block.set',
                at: focusTextBlock.path,
                props: {
                  markDefs: [
                    ...(focusTextBlock.node.markDefs ?? []),
                    ...markDefs,
                  ],
                },
              }),
            ]
          : []),
        raise({
          type: 'insert.child',
          child: {
            _type: snapshot.context.schema.span.name,
            text: event.text,
            marks: [
              ...(event.decorators ?? []),
              ...markDefs.map((markDef) => markDef._key),
            ],
          },
        }),
      ],
    ],
  }),

  /**
   * If there's an expanded selection, then we delete the selection before we
   * insert the text.
   */
  defineBehavior({
    on: 'insert.text',
    guard: ({snapshot}) => {
      return isSelectionExpanded(snapshot)
    },
    actions: [({event}) => [raise({type: 'delete'}), raise(event)]],
  }),

  /**
   * If there's no selection, then we select the end of the editor before we
   * we insert the text.
   */
  defineBehavior({
    on: 'insert.text',
    guard: ({snapshot}) => {
      if (snapshot.context.selection) {
        return false
      }

      const lastBlok = getLastBlock(snapshot)

      if (!lastBlok) {
        return false
      }

      const endPoint = getBlockEndPoint({
        context: snapshot.context,
        block: lastBlok,
      })
      return {endPoint}
    },
    actions: [
      ({event}, {endPoint}) => [
        raise({
          type: 'select',
          at: {
            anchor: endPoint,
            focus: endPoint,
          },
        }),
        raise(event),
      ],
    ],
  }),
]
